import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
    Send, X, Loader2, ChevronDown, Trash2, Edit2, Check, XCircle
} from 'lucide-react';

// API Gateway endpoint for zone chat
const API_BASE_URL = 'https://4y5ppe8y0j.execute-api.us-west-2.amazonaws.com/prod';

const POLL_INTERVAL_MS = 2000;

/**
 * ZoneGroupChat - Clean, responsive group chat for a zone.
 * - Optimistic send (message appears instantly, reconciled with the server)
 * - 2s delta polling, deduped by message_id
 * - Full-screen on mobile with visualViewport keyboard handling
 */
export const ZoneGroupChat = ({
    zoneName,
    zoneId,
    chatId,
    userName,
    userPhoto,
    onClose
}) => {
    const [messages, setMessages] = useState([]);
    const [newMessage, setNewMessage] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [isSending, setIsSending] = useState(false);
    const [showScrollButton, setShowScrollButton] = useState(false);
    const [editingMessageId, setEditingMessageId] = useState(null);
    const [editedText, setEditedText] = useState('');
    const [showWarning, setShowWarning] = useState(false);
    const [countdown, setCountdown] = useState(10);
    // On mobile, pin the modal to the visual viewport so the keyboard
    // shrinks the chat instead of shoving it off-screen.
    const [viewportHeight, setViewportHeight] = useState(null);
    const messagesEndRef = useRef(null);
    const messagesContainerRef = useRef(null);
    const inputRef = useRef(null);
    const inactivityTimerRef = useRef(null);
    const warningTimerRef = useRef(null);
    const countdownIntervalRef = useRef(null);
    const lastMessageCountRef = useRef(0);

    const scrollToBottom = (smooth = true) => {
        messagesEndRef.current?.scrollIntoView({
            behavior: smooth ? 'smooth' : 'auto'
        });
    };

    const handleScroll = () => {
        if (!messagesContainerRef.current) return;
        const { scrollTop, scrollHeight, clientHeight } = messagesContainerRef.current;
        const isNearBottom = scrollHeight - scrollTop - clientHeight < 100;
        setShowScrollButton(!isNearBottom);
    };

    const isNearBottom = () => {
        if (!messagesContainerRef.current) return true;
        const { scrollTop, scrollHeight, clientHeight } = messagesContainerRef.current;
        return scrollHeight - scrollTop - clientHeight < 150;
    };

    // Merge server messages into local state: dedupe by message_id, keep
    // pending (optimistic) messages, sort chronologically.
    const mergeMessages = (prev, incoming) => {
        const byId = new Map();
        for (const m of prev) byId.set(m.message_id, m);
        for (const m of incoming) {
            const existing = byId.get(m.message_id);
            // Don't let a server copy clobber a pending optimistic message
            // until it's actually confirmed.
            if (existing?.pending && !m.pending) byId.delete(existing.message_id);
            byId.set(m.message_id, { ...(existing || {}), ...m });
        }
        return Array.from(byId.values()).sort((a, b) => a.timestamp - b.timestamp);
    };

    const loadMessages = async (initial = true) => {
        if (initial) setIsLoading(true);
        try {
            const response = await fetch(
                `${API_BASE_URL}/zone-chat-messages?zone_id=${zoneId}&limit=50`,
                { method: 'GET' }
            );
            const data = await response.json();
            const serverMessages = data.messages || [];

            if (initial) {
                setMessages(serverMessages);
                setTimeout(() => scrollToBottom(false), 100);
                return;
            }

            setMessages(prev => {
                if (prev.length === 0) return serverMessages;

                // Latest confirmed timestamp we already have (ignore pending).
                const latestTimestamp = prev.reduce(
                    (max, m) => (!m.pending && m.timestamp > max ? m.timestamp : max),
                    0
                );
                const existingIds = new Set(prev.map(m => m.message_id));
                const trulyNew = serverMessages.filter(
                    m => m.timestamp > latestTimestamp && !existingIds.has(m.message_id)
                );
                if (trulyNew.length === 0) return prev;

                const stick = isNearBottom();
                if (stick) setTimeout(() => scrollToBottom(), 50);
                return mergeMessages(prev, trulyNew);
            });
        } catch (error) {
            console.error('Error loading messages:', error);
        } finally {
            if (initial) setIsLoading(false);
        }
    };

    const sendMessage = async () => {
        const text = newMessage.trim();
        if (!text || isSending) return;

        setIsSending(true);
        setNewMessage('');

        // Optimistic message — shows instantly, marked pending.
        const tempId = `temp-${Date.now()}`;
        const optimistic = {
            message_id: tempId,
            timestamp: Date.now(),
            user_id: String(chatId),
            user_name: userName,
            user_photo: userPhoto,
            message: text,
            pending: true
        };
        setMessages(prev => [...prev, optimistic]);
        setTimeout(() => scrollToBottom(), 50);

        try {
            const response = await fetch(`${API_BASE_URL}/send-zone-chat-message`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    zone_id: zoneId,
                    chat_id: chatId,
                    message: text,
                    user_name: userName,
                    user_photo: userPhoto
                })
            });

            if (!response.ok) throw new Error(`Send failed: ${response.status}`);

            const data = await response.json();
            const saved = data.data;
            // Swap the optimistic message for the server's authoritative copy.
            setMessages(prev =>
                prev.map(m => (m.message_id === tempId ? { ...saved } : m))
            );
        } catch (error) {
            console.error('Error sending message:', error);
            // Roll back the optimistic message and restore the draft.
            setMessages(prev => prev.filter(m => m.message_id !== tempId));
            setNewMessage(text);
        } finally {
            setIsSending(false);
        }
    };

    const deleteMessage = async (message) => {
        if (!confirm('האם אתה בטוח שברצונך למחוק הודעה זו?')) return;
        try {
            const response = await fetch(`${API_BASE_URL}/delete-zone-message`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    zone_id: zoneId,
                    message_id: message.message_id,
                    timestamp: message.timestamp,
                    user_id: chatId
                })
            });
            if (response.ok) {
                setMessages(prev => prev.filter(m => m.message_id !== message.message_id));
            } else {
                const error = await response.json();
                alert(error.reason || 'שגיאה במחיקת ההודעה');
            }
        } catch (error) {
            console.error('Error deleting message:', error);
            alert('שגיאה במחיקת ההודעה');
        }
    };

    const startEdit = (message) => {
        setEditingMessageId(message.message_id);
        setEditedText(message.message);
    };

    const cancelEdit = () => {
        setEditingMessageId(null);
        setEditedText('');
    };

    const saveEdit = async (message) => {
        if (!editedText.trim()) return;
        try {
            const response = await fetch(`${API_BASE_URL}/edit-zone-message`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    zone_id: zoneId,
                    message_id: message.message_id,
                    timestamp: message.timestamp,
                    user_id: chatId,
                    new_message: editedText.trim()
                })
            });
            if (response.ok) {
                setMessages(prev =>
                    prev.map(m =>
                        m.message_id === message.message_id
                            ? { ...m, message: editedText.trim(), edited: true, edited_at: Date.now() }
                            : m
                    )
                );
                setEditingMessageId(null);
                setEditedText('');
            } else {
                const error = await response.json();
                alert(error.reason || 'שגיאה בעריכת ההודעה');
            }
        } catch (error) {
            console.error('Error editing message:', error);
            alert('שגיאה בעריכת ההודעה');
        }
    };

    const resetInactivityTimer = () => {
        if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
        if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
        if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);

        setShowWarning(false);
        setCountdown(10);

        warningTimerRef.current = setTimeout(() => {
            setShowWarning(true);
            setCountdown(10);
            countdownIntervalRef.current = setInterval(() => {
                setCountdown(prev => {
                    if (prev <= 1) {
                        clearInterval(countdownIntervalRef.current);
                        return 0;
                    }
                    return prev - 1;
                });
            }, 1000);
        }, 50000);

        inactivityTimerRef.current = setTimeout(() => {
            onClose();
        }, 60000);
    };

    // Poll for new messages.
    useEffect(() => {
        loadMessages();
        const interval = setInterval(() => loadMessages(false), POLL_INTERVAL_MS);
        return () => clearInterval(interval);
    }, [zoneId]);

    // Reset the auto-close timer whenever new messages arrive.
    useEffect(() => {
        if (messages.length > lastMessageCountRef.current) {
            resetInactivityTimer();
            lastMessageCountRef.current = messages.length;
        }
    }, [messages]);

    useEffect(() => {
        resetInactivityTimer();
        return () => {
            if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
            if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
            if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
        };
    }, []);

    useEffect(() => {
        inputRef.current?.focus();
    }, []);

    // Keep the modal sized to the visual viewport on mobile so the on-screen
    // keyboard shrinks the chat (input stays pinned above it) instead of
    // pushing the whole modal upward.
    useEffect(() => {
        const vv = window.visualViewport;
        if (!vv) return;
        const sync = () => {
            setViewportHeight(window.innerWidth < 640 ? vv.height : null);
        };
        sync();
        vv.addEventListener('resize', sync);
        vv.addEventListener('scroll', sync);
        return () => {
            vv.removeEventListener('resize', sync);
            vv.removeEventListener('scroll', sync);
        };
    }, []);

    const formatTime = (timestamp) => {
        const date = new Date(timestamp);
        const diffMs = new Date() - date;
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMs / 3600000);
        const diffDays = Math.floor(diffMs / 86400000);

        if (diffMins < 1) return 'כרגע';
        if (diffMins < 60) return `לפני ${diffMins} דקות`;
        if (diffHours < 24) return `לפני ${diffHours} שעות`;
        if (diffDays < 7) return `לפני ${diffDays} ימים`;
        return date.toLocaleDateString('he-IL', { day: 'numeric', month: 'short' });
    };

    const groupedMessages = messages.reduce((groups, message, index) => {
        const prevMessage = messages[index - 1];
        const shouldGroup = prevMessage &&
            prevMessage.user_id === message.user_id &&
            (new Date(message.timestamp) - new Date(prevMessage.timestamp)) < 60000;

        if (shouldGroup && groups.length > 0) {
            groups[groups.length - 1].messages.push(message);
        } else {
            groups.push({
                user_id: message.user_id,
                user_name: message.user_name,
                user_photo: message.user_photo,
                messages: [message]
            });
        }
        return groups;
    }, []);

    const isOwnMessage = (userId) => String(userId) === String(chatId);

    return createPortal(
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[99999] flex sm:items-center sm:justify-center sm:p-4 bg-black/40 sm:backdrop-blur-sm"
            onClick={onClose}
        >
            <motion.div
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 24 }}
                transition={{ duration: 0.2 }}
                onClick={(e) => e.stopPropagation()}
                style={viewportHeight ? { height: `${viewportHeight}px` } : undefined}
                className="relative w-full sm:max-w-2xl h-[100dvh] sm:h-[85vh] sm:max-h-[calc(100vh-8rem)] bg-surface-muted sm:rounded-2xl shadow-2xl overflow-hidden flex flex-col"
            >
                {/* Header */}
                <div className="flex items-center justify-between gap-3 bg-brand-600 px-4 py-3 sm:px-6 sm:py-4">
                    <div className="flex items-center gap-3 min-w-0">
                        <div className="w-10 h-10 rounded-2xl bg-white/15 !text-white flex items-center justify-center flex-shrink-0 text-lg font-semibold">
                            {zoneName?.[0] || '#'}
                        </div>
                        <div className="min-w-0">
                            <h2 className="font-heading text-lg sm:text-xl font-bold tracking-tight !text-white truncate leading-tight">
                                {zoneName}
                            </h2>
                            <p className="text-xs !text-white/70">צ'אט קהילתי</p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="w-9 h-9 rounded-full !text-white hover:bg-white/15 transition-colors flex items-center justify-center flex-shrink-0"
                        aria-label="סגירה"
                    >
                        <X className="w-5 h-5" strokeWidth={2} />
                    </button>
                </div>

                {/* Messages */}
                <div
                    ref={messagesContainerRef}
                    onScroll={handleScroll}
                    className="flex-1 overflow-y-auto overscroll-contain px-3 sm:px-6 py-4 space-y-3"
                >
                    {isLoading ? (
                        <div className="flex items-center justify-center h-full">
                            <Loader2 className="w-7 h-7 text-brand-500 animate-spin" />
                        </div>
                    ) : messages.length === 0 ? (
                        <div className="flex flex-col items-center justify-center h-full text-center px-6">
                            <div className="w-14 h-14 rounded-full bg-brand-100 flex items-center justify-center mb-3">
                                <Send className="w-6 h-6 text-brand-600" strokeWidth={2} />
                            </div>
                            <h3 className="text-base font-semibold text-ink-strong mb-1">אין עדיין הודעות</h3>
                            <p className="text-sm text-ink-muted">היו הראשונים לשתף משהו באזור!</p>
                        </div>
                    ) : (
                        <>
                            {groupedMessages.map((group, groupIndex) => {
                                const own = isOwnMessage(group.user_id);
                                return (
                                    <motion.div
                                        key={`group-${groupIndex}`}
                                        initial={{ opacity: 0, y: 8 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ duration: 0.15 }}
                                        className={`flex gap-2.5 ${own ? 'flex-row-reverse' : 'flex-row'}`}
                                    >
                                        <div className="flex-shrink-0">
                                            {group.user_photo ? (
                                                <>
                                                    <img
                                                        src={group.user_photo}
                                                        alt={group.user_name}
                                                        className="w-9 h-9 rounded-full object-cover"
                                                        onError={(e) => {
                                                            e.target.style.display = 'none';
                                                            e.target.nextSibling.style.display = 'flex';
                                                        }}
                                                    />
                                                    <div
                                                        className="w-9 h-9 rounded-full bg-brand-500 items-center justify-center text-white font-semibold text-sm"
                                                        style={{ display: 'none' }}
                                                    >
                                                        {group.user_name?.[0] || 'א'}
                                                    </div>
                                                </>
                                            ) : (
                                                <div className="w-9 h-9 rounded-full bg-brand-500 flex items-center justify-center text-white font-semibold text-sm">
                                                    {group.user_name?.[0] || 'א'}
                                                </div>
                                            )}
                                        </div>

                                        <div className={`flex-1 flex flex-col gap-1 ${own ? 'items-end' : 'items-start'}`}>
                                            <p className="text-xs font-medium text-ink-muted px-1">
                                                {group.user_name || 'אנונימי'}
                                            </p>

                                            {group.messages.map((message) => (
                                                <div
                                                    key={message.message_id}
                                                    className={`group relative max-w-[80%] sm:max-w-[70%] px-3.5 py-2 text-sm leading-relaxed ${own
                                                        ? 'bg-brand-600 text-white rounded-2xl rounded-tr-md'
                                                        : 'bg-white text-ink-strong rounded-2xl rounded-tl-md border border-line'
                                                        } ${message.pending ? 'opacity-70' : ''}`}
                                                >
                                                    {own && !message.pending && editingMessageId !== message.message_id && (
                                                        <div className="absolute -top-3.5 -right-2 sm:opacity-0 group-hover:opacity-100 transition-opacity flex gap-1.5">
                                                            <button
                                                                onClick={() => startEdit(message)}
                                                                className="w-6 h-6 rounded-full bg-white text-ink-muted hover:text-brand-600 flex items-center justify-center shadow border border-line"
                                                                title="עריכה"
                                                            >
                                                                <Edit2 size={12} strokeWidth={2.5} />
                                                            </button>
                                                            <button
                                                                onClick={() => deleteMessage(message)}
                                                                className="w-6 h-6 rounded-full bg-white text-ink-muted hover:text-danger-500 flex items-center justify-center shadow border border-line"
                                                                title="מחיקה"
                                                            >
                                                                <Trash2 size={12} strokeWidth={2.5} />
                                                            </button>
                                                        </div>
                                                    )}

                                                    {editingMessageId === message.message_id ? (
                                                        <div className="space-y-2">
                                                            <textarea
                                                                value={editedText}
                                                                onChange={(e) => setEditedText(e.target.value)}
                                                                className="w-full px-2 py-1 rounded-lg bg-white/90 text-ink-strong text-sm border border-line-strong focus:outline-none focus:ring-2 focus:ring-brand-400"
                                                                rows={3}
                                                                autoFocus
                                                            />
                                                            <div className="flex gap-2 justify-end">
                                                                <button
                                                                    onClick={cancelEdit}
                                                                    className="px-3 py-1 rounded-lg bg-surface-sunken text-ink text-xs hover:bg-line flex items-center gap-1"
                                                                >
                                                                    <XCircle size={14} /> ביטול
                                                                </button>
                                                                <button
                                                                    onClick={() => saveEdit(message)}
                                                                    className="px-3 py-1 rounded-lg bg-brand-600 text-white text-xs hover:bg-brand-700 flex items-center gap-1"
                                                                >
                                                                    <Check size={14} /> שמור
                                                                </button>
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        <>
                                                            <p className="break-words whitespace-pre-wrap">
                                                                {message.message}
                                                            </p>
                                                            <p className={`text-[10px] mt-0.5 flex items-center gap-1 ${own ? 'text-white/60' : 'text-ink-subtle'}`}>
                                                                {message.pending ? (
                                                                    <>
                                                                        <Loader2 size={10} className="animate-spin" /> שולח…
                                                                    </>
                                                                ) : (
                                                                    <>
                                                                        {formatTime(message.timestamp)}
                                                                        {message.edited && <span className="italic">(נערך)</span>}
                                                                    </>
                                                                )}
                                                            </p>
                                                        </>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    </motion.div>
                                );
                            })}
                            <div ref={messagesEndRef} />
                        </>
                    )}
                </div>

                {/* Scroll to bottom */}
                <AnimatePresence>
                    {showScrollButton && (
                        <motion.button
                            initial={{ opacity: 0, y: 8 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: 8 }}
                            onClick={() => scrollToBottom()}
                            className="absolute bottom-24 left-1/2 -translate-x-1/2 w-9 h-9 rounded-full bg-white shadow-md border border-line flex items-center justify-center"
                        >
                            <ChevronDown className="w-5 h-5 text-brand-600" strokeWidth={2.5} />
                        </motion.button>
                    )}
                </AnimatePresence>

                {/* Inactivity warning */}
                <AnimatePresence>
                    {showWarning && (
                        <motion.div
                            initial={{ opacity: 0, y: -12 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -12 }}
                            className="absolute top-20 left-1/2 -translate-x-1/2 bg-warning-500 text-white px-5 py-2.5 rounded-xl shadow-lg flex items-center gap-3 z-50"
                        >
                            <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center font-bold">
                                {countdown}
                            </div>
                            <p className="text-sm font-medium">
                                הצ'אט ייסגר בעוד {countdown} שניות
                            </p>
                        </motion.div>
                    )}
                </AnimatePresence>

                {/* Input */}
                <div
                    className="border-t border-line bg-white px-3 sm:px-6 pt-3"
                    style={{ paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom))' }}
                >
                    {chatId && chatId !== 'guest' ? (
                        <div className="flex gap-2 items-end">
                            <textarea
                                ref={inputRef}
                                value={newMessage}
                                onChange={(e) => setNewMessage(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' && !e.shiftKey) {
                                        e.preventDefault();
                                        sendMessage();
                                    }
                                }}
                                placeholder="כתבו הודעה…"
                                rows={1}
                                className="flex-1 px-4 py-3 rounded-3xl bg-surface-muted border border-line focus:border-brand-400 focus:ring-2 focus:ring-brand-400/20 outline-none resize-none text-sm text-ink-strong placeholder:text-ink-subtle transition-all"
                                style={{ minHeight: '44px', maxHeight: '120px' }}
                            />
                            <button
                                onClick={sendMessage}
                                disabled={!newMessage.trim() || isSending}
                                className={`w-11 h-11 rounded-full flex items-center justify-center flex-shrink-0 transition-colors ${newMessage.trim() && !isSending
                                    ? 'bg-brand-600 text-white hover:bg-brand-700'
                                    : 'bg-surface-sunken text-ink-subtle cursor-not-allowed'
                                    }`}
                                aria-label="שליחה"
                            >
                                {isSending ? (
                                    <Loader2 className="w-5 h-5 animate-spin" strokeWidth={2.5} />
                                ) : (
                                    <Send className="w-5 h-5" strokeWidth={2.5} />
                                )}
                            </button>
                        </div>
                    ) : (
                        <div className="text-center py-3">
                            <p className="text-sm text-ink-muted">התחברו כדי לשלוח הודעות</p>
                        </div>
                    )}
                </div>
            </motion.div>
        </motion.div>,
        document.body
    );
};
